import React, { useState, useEffect } from 'react';
import axios from 'axios';

function FilingView() {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const params = new URLSearchParams(window.location.search);
  const filingId = params.get('id');
  // ?admin=1 — opened from the admin dashboard: use the admin login and admin endpoint
  const isAdmin = params.get('admin') === '1';
  const token = localStorage.getItem(isAdmin ? 'adminToken' : 'token');
  const detailUrl = isAdmin ? `/admin/filings/${filingId}` : `/filings/my-filings/${filingId}`;
  const backUrl = isAdmin ? '/admin/dashboard' : '/my-filings';

  useEffect(() => {
    if (!token || !filingId) {
      window.location.href = '/login';
      return;
    }
    fetchDetail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?print=1 — open the print dialog as soon as the filing has rendered (saves a second click)
  useEffect(() => {
    if (detail && params.get('print') === '1') window.print();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const fetchDetail = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}${detailUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail(response.data);
    } catch (err) {
      setError('Could not load this filing.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.container}><p style={styles.loading}>Loading...</p></div>;
  if (error) return <div style={styles.container}><p style={styles.error}>{error}</p></div>;
  if (!detail) return null;

  const { filing, reported_items, signature } = detail;
  const isForm2 = filing.form_type === 'form_2';

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        {/* Print/Back buttons — hidden when printing */}
        <div style={styles.actionRow} className="no-print">
          <button style={styles.backButton} onClick={() => window.location.href = backUrl}>
            ← Back to {isAdmin ? 'Dashboard' : 'My Filings'}
          </button>
          <button style={styles.printButton} onClick={() => window.print()}>
            Print / Save as PDF
          </button>
        </div>

        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>MAtownforms</h1>
          <p style={styles.formLabel}>
            {isForm2 ? 'State Tax Form 2 — Return of Personal Property' : 'State Tax Form 2HF — Second Residence Form of List'}
          </p>
          <p style={styles.meta}>
            {filing.town_or_city} &nbsp;|&nbsp; Fiscal Year {filing.fiscal_year} &nbsp;|&nbsp; Submitted {filing.submitted_at ? new Date(filing.submitted_at).toLocaleDateString() : '—'}
          </p>
        </div>

        {/* Filing Number */}
        <div style={styles.filingNumberBox}>
          <span style={styles.filingNumberLabel}>Filing Number: </span>
          <span style={styles.filingNumber}>{filing.filing_reference}</span>
        </div>

        {/* Filer Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Filer Information</h3>
          <div style={styles.grid}>
            <div style={styles.field}><strong>Owner / Taxpayer Name:</strong> {filing.owner_name}</div>
            {filing.business_name && <div style={styles.field}><strong>Business Name:</strong> {filing.business_name}</div>}
            {filing.entity_type && <div style={styles.field}><strong>Entity Type:</strong> {filing.entity_type}</div>}
            {filing.fid_number && <div style={styles.field}><strong>Federal ID Number:</strong> {filing.fid_number}</div>}
            {filing.nature_of_business && <div style={styles.field}><strong>Nature of Business:</strong> {filing.nature_of_business}</div>}
            {filing.business_address && <div style={styles.field}><strong>Business Address:</strong> {filing.business_address}</div>}
            {filing.legal_residence && <div style={styles.field}><strong>Legal Residence:</strong> {filing.legal_residence}</div>}
            {filing.property_ownership && <div style={styles.field}><strong>Property Ownership:</strong> {filing.property_ownership}</div>}
            <div style={styles.field}><strong>Property Location:</strong> {filing.property_location}</div>
            {filing.mailing_address && <div style={styles.field}><strong>Mailing Address:</strong> {filing.mailing_address}</div>}
            <div style={styles.field}><strong>Phone:</strong> {filing.phone}</div>
            <div style={styles.field}><strong>Email:</strong> {filing.email}</div>
          </div>
        </div>

        {/* Reported Items */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Reported Items ({reported_items.length})</h3>
          {reported_items.length === 0 ? (
            <p style={styles.noItems}>No items reported.</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Schedule', 'Description', 'Own/Other', 'Qty', 'Year Mfg', 'Year Purch', 'Purchase Price', 'Est. Market Value'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reported_items.map((item, i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                    <td style={styles.td}>{item.schedule}</td>
                    <td style={styles.td}>{item.description}</td>
                    <td style={styles.td}>{item.own_or_other}</td>
                    <td style={styles.td}>{item.quantity || '—'}</td>
                    <td style={styles.td}>{item.year_of_manufacture || '—'}</td>
                    <td style={styles.td}>{item.year_of_purchase || '—'}</td>
                    <td style={styles.td}>{item.purchase_price ? `$${item.purchase_price.toLocaleString()}` : '—'}</td>
                    <td style={styles.td}>{item.estimated_market_value ? `$${item.estimated_market_value.toLocaleString()}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Signature */}
        {signature && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>E-Signature</h3>
            <div style={styles.grid}>
              <div style={styles.field}><strong>Signed By:</strong> {signature.signer_full_name}</div>
              {signature.signer_title && <div style={styles.field}><strong>Title:</strong> {signature.signer_title}</div>}
              <div style={styles.field}><strong>Signed At:</strong> {signature.signed_at ? new Date(signature.signed_at).toLocaleString() : '—'}</div>
              <div style={styles.field}><strong>Filing Number:</strong> {filing.filing_reference}</div>
            </div>
            <p style={styles.certificationText}>
              This list, prepared or examined by me, includes all taxable personal property owned or held by the maker
              of this list on January 1 and to the best of my knowledge and belief, it and all accompanying schedules
              and statements are true, correct and complete. Subscribed under the penalties of perjury.
            </p>
          </div>
        )}

        <p style={styles.footer}>
          MAtownforms — matownforms.com — Filing Number: {filing.filing_reference}
        </p>

      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f4f6f9',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: '#ffffff',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    maxWidth: '900px',
    margin: '0 auto',
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #1a3a5c',
    color: '#1a3a5c',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  printButton: {
    padding: '8px 16px',
    backgroundColor: '#1a3a5c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
    borderBottom: '2px solid #1a3a5c',
    paddingBottom: '16px',
  },
  title: {
    color: '#1a3a5c',
    fontSize: '24px',
    marginBottom: '4px',
  },
  formLabel: {
    fontSize: '16px',
    color: '#444',
    marginBottom: '4px',
  },
  meta: {
    fontSize: '13px',
    color: '#666',
  },
  filingNumberBox: {
    backgroundColor: '#f4f6f9',
    padding: '12px 16px',
    borderRadius: '4px',
    marginBottom: '24px',
    textAlign: 'center',
  },
  filingNumberLabel: {
    fontSize: '14px',
    color: '#666',
  },
  filingNumber: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a3a5c',
    fontFamily: 'monospace',
  },
  section: {
    marginBottom: '28px',
    borderTop: '1px solid #eee',
    paddingTop: '16px',
  },
  sectionTitle: {
    color: '#1a3a5c',
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  },
  field: {
    fontSize: '14px',
    color: '#333',
    padding: '4px 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    backgroundColor: '#1a3a5c',
    color: '#fff',
    padding: '8px 10px',
    textAlign: 'left',
    fontWeight: '600',
  },
  td: {
    padding: '8px 10px',
    borderBottom: '1px solid #eee',
    color: '#333',
  },
  noItems: {
    color: '#666',
    fontSize: '14px',
  },
  certificationText: {
    fontSize: '12px',
    color: '#555',
    fontStyle: 'italic',
    lineHeight: '1.6',
    marginTop: '12px',
    borderTop: '1px solid #eee',
    paddingTop: '12px',
  },
  footer: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#999',
    marginTop: '24px',
    borderTop: '1px solid #eee',
    paddingTop: '16px',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    padding: '40px',
  },
  error: {
    textAlign: 'center',
    color: '#cc0000',
    padding: '40px',
  },
};

export default FilingView;
